import React from 'react';
import {
    Input,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    useToast,
    Button,
    useDisclosure,
    Box,
    List,
    ListItem,
    VStack,
    HStack,
    keyframes,
} from '@chakra-ui/react';
import ExpandDown from 'components/custom-components/icons/ExpandDown';
import ExpandUp from 'components/custom-components/icons/ExpandUp';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { PauseCircle, PlayCircle, Check } from 'lucide-react';
import { IAgentVoices } from 'types';
import { useError } from 'error-provider/ErrorProvider';
import useServerApi from 'hooks/useServerApi';
import { PATHS } from 'router/routes';
import { useNavigate } from 'react-router-dom';

interface Props {
    agentId: string;
    agentVoices: IAgentVoices;
    name: string | null | undefined;
    allowMultipleSelect?: boolean;
    currentVoices?: string[];
}

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(91, 78, 212, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(91, 78, 212, 0); }
  100% { box-shadow: 0 0 0 0 rgba(91, 78, 212, 0); }
`;

const VoicePicker = ({ agentId, agentVoices, allowMultipleSelect, currentVoices }: Props) => {
    const toast = useToast();
    const navigate = useNavigate();

    const audio = useRef<HTMLAudioElement | null>(null);
    const { showError } = useError();
    const [{ post }] = useServerApi();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [selectedVoices, setSelectedVoices] = useState<string[]>([]);
    const [savedVoices, setSavedVoices] = useState<string[]>([]);
    const [isPlay, setIsPlay] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSavedState, setShowSavedState] = useState(false);

    useEffect(() => {
        if (currentVoices) {
            setSelectedVoices(currentVoices);
            setSavedVoices(currentVoices);
        }
    }, [currentVoices]);

    const currentVoice = useMemo(() => {
        return selectedVoices.length > 0
            ? agentVoices.find((voice) => voice.name === selectedVoices[0])
            : agentVoices[0];
    }, [agentVoices, selectedVoices]);



    async function onChangeVoice(voices: string[]) {
        if (voices.length === 0) {
            toast({
                title: 'No voice selected',
                description: 'Please select at least one voice.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        try {
            setIsLoading(true);
            const voice_ids = agentVoices.filter(v => voices.includes(v.name)).map(v => v.id);
            const [success, res] = await post(`/rest_endpoints/agents/crud/voices/${parseInt(agentId)}/save`, { voice_ids });
            if (success) {
                setSavedVoices(voices);
                setShowSavedState(true);
                setTimeout(async () => {
                    onClose();
                    await new Promise(resolve => setTimeout(resolve, 500));
                    setShowSavedState(false);
                }, 2000);
                toast({
                    title: 'Voices updated successfully',
                    status: 'success',
                    isClosable: true,
                });
            } else {
                toast({
                    title: res?.message || 'Failed to save voices',
                    status: 'error',
                    isClosable: true,
                });
            }
        } catch (error) {
            showError('Failed to save voices', error as Error);
        } finally {
            setIsLoading(false);
        }
    }



    useEffect(() => {
        if (audio.current) {
            if (isPlay) {
                audio.current.play();
            } else {
                audio.current.pause();
            }
        }
    }, [isPlay, audio]);

    // Filter voices by search query
    const filteredVoices = useMemo(() => {
        return agentVoices.filter((voice) => {
            const query = searchQuery.toLowerCase();
            const matchesName = voice.name.toLowerCase().includes(query);
            const matchesDescription = voice.description?.toLowerCase().includes(query) || false;
            return matchesName || matchesDescription;
        });
    }, [agentVoices, searchQuery]);

    const hasUnsavedChanges = useMemo(() => {
        if (selectedVoices.length !== savedVoices.length) return true;
        const sortedSelected = [...selectedVoices].sort();
        const sortedSaved = [...savedVoices].sort();
        return !sortedSelected.every((voice, idx) => voice === sortedSaved[idx]);
    }, [selectedVoices, savedVoices]);

    const handleVoiceToggle = (voiceName: string) => {
        if (allowMultipleSelect) {
            setSelectedVoices((prev: string[]) => {
                if (prev.includes(voiceName)) {
                    return prev.filter((n) => n !== voiceName);
                }
                if (prev.length >= 5) {
                    toast({
                        title: 'Limit reached',
                        description: 'You can select up to 5 voices only.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                    return prev;
                }
                return [...prev, voiceName];
            });
        } else {
            setSelectedVoices([voiceName]);
        }
    };

    const getVoicePersonality = (voice: any) => {
        // Use description if available, otherwise fallback
        return voice.description || 'Warm & professional';
    };

    const VoiceRow = ({ voice }: { voice: any }) => {
        const isSelected = allowMultipleSelect
            ? selectedVoices.includes(voice.name)
            : voice.name === selectedVoices[0];

        return (
            <ListItem
                p="12px"
                cursor="pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsPlay(false);
                    handleVoiceToggle(voice.name);
                }}
                bg={isSelected ? 'purple.50' : 'white'}
                borderBottom="1px solid"
                borderColor="gray.200"
                _hover={{ bg: isSelected ? 'purple.100' : 'gray.50' }}
                transition="all 0.2s"
            >
                <HStack spacing={3} align="center">
                    {/* Checkbox indicator */}
                    <Box
                        w="20px"
                        h="20px"
                        border="2px solid"
                        borderColor={isSelected ? 'purple.500' : 'gray.300'}
                        borderRadius="4px"
                        bg={isSelected ? 'purple.500' : 'white'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                    >
                        {isSelected && <Check size={14} color="white" />}
                    </Box>

                    {/* Voice info */}
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontWeight="600" fontSize="14px" color="gray.800">
                            {voice.name}
                        </Text>
                        <Text fontSize="12px" color="gray.600" isTruncated maxW="250px">
                            {getVoicePersonality(voice)}
                        </Text>
                    </VStack>

                    {/* Play button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (voice.previewUrl) {
                                setIsPlay((prev) => !prev);
                            }
                        }}
                        disabled={!voice.previewUrl}
                        flexShrink={0}
                    >
                        {isPlay && currentVoice?.name === voice.name ? (
                            <PauseCircle size={20} color="#5B4ED4" />
                        ) : (
                            <PlayCircle size={20} color="#5B4ED4" />
                        )}
                    </Button>
                </HStack>
            </ListItem>
        );
    };

    return (
        <Box w="100%" maxW="500px">
            {/* Voice picker popover */}
            <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen} placement="bottom-start">
                <PopoverTrigger>
                    <Button
                        w="100%"
                        justifyContent="space-between"
                        rightIcon={isOpen ? <ExpandUp /> : <ExpandDown />}
                        variant="outline"
                        borderColor="gray.300"
                        bg="white"
                        fontWeight="normal"
                        color={selectedVoices.length === 0 ? 'gray.500' : 'gray.800'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPlay(false);
                        }}
                    >
                        {selectedVoices.length === 0
                            ? 'Select voices...'
                            : selectedVoices.length === 1
                                ? selectedVoices[0]
                                : 'Multiple Voices Selected'}
                    </Button>
                </PopoverTrigger>

                <PopoverContent w="400px" maxW="90vw">
                    {/* Search input */}
                    <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                        <Input
                            placeholder="Search by name or description..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="sm"
                        />
                    </Box>

                    {/* Voice list */}
                    <PopoverBody p={0} maxH="400px" overflowY="auto">
                        {filteredVoices.length > 0 ? (
                            <List spacing={0}>
                                {filteredVoices.map((voice) => (
                                    <VoiceRow key={voice.id} voice={voice} />
                                ))}
                            </List>
                        ) : (
                            <Box p={4} textAlign="center">
                                <Text color="gray.500">No voices found</Text>
                            </Box>
                        )}
                    </PopoverBody>

                    {/* Save button with unsaved state - inside popover */}
                    <Box p={3} borderTop="1px solid" borderColor="gray.200">
                        <Button
                            size="lg"
                            colorScheme="purple"
                            w="100%"
                            isLoading={isLoading}
                            isDisabled={!hasUnsavedChanges && !showSavedState}
                            onClick={() => onChangeVoice(selectedVoices)}
                            leftIcon={showSavedState ? <Check size={18} /> : undefined}
                            animation={hasUnsavedChanges && !isLoading ? `${pulseAnimation} 2s infinite` : undefined}
                            position="relative"
                        >
                            {showSavedState ? 'Saved!' : hasUnsavedChanges ? 'Save Changes' : 'Saved'}
                            {hasUnsavedChanges && !isLoading && (
                                <Box
                                    position="absolute"
                                    top="50%"
                                    right="16px"
                                    transform="translateY(-50%)"
                                    w="8px"
                                    h="8px"
                                    borderRadius="full"
                                    bg="white"
                                    animation={`${pulseAnimation} 2s infinite`}
                                />
                            )}
                        </Button>

                        {/* Upgrade CTA - inside popover */}
                        {!allowMultipleSelect && (
                            <Box textAlign="center" mt={2}>
                                <Text fontSize="sm" color="gray.600" mb={2}>
                                    Want to pick multiple voices? Upgrade your plan to unlock multi-select.
                                </Text>
                                <Button
                                    size="sm"
                                    colorScheme="purple"
                                    variant="outline"
                                    onClick={() => navigate(PATHS.SUBSCRIPTION)}
                                >
                                    Upgrade Plan
                                </Button>
                            </Box>
                        )}
                    </Box>
                </PopoverContent>
            </Popover>

            {/* Audio element for preview playback */}
            {currentVoice?.previewUrl && (
                <audio ref={audio} onEnded={() => setIsPlay(false)} src={currentVoice?.previewUrl ?? ''} autoPlay={false} />
            )}
        </Box>
    );
}

export default memo(VoicePicker)
